import { useState, useContext } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from 'src/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Label } from 'src/components/ui/label';
import { Input } from 'src/components/ui/input';
import { Textarea } from 'src/components/ui/textarea';
import { Checkbox } from 'src/components/ui/checkbox';
import { PlusIcon, PencilIcon, TrashIcon } from '@phosphor-icons/react';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import { CompanyDTO } from 'src/api/core/companies/useCompany';
import {
  useContacts,
  Contact,
} from 'src/api/core/communications/contacts/useContacts';
import { useCreateContact } from 'src/api/core/communications/contacts/useCreateContact';
import { useUpdateContact } from 'src/api/core/communications/contacts/useUpdateContact';
import { useDeleteContact } from 'src/api/core/communications/contacts/useDeleteContact';
import { AuthContext } from 'src/context/auth.context';
import { useViewMode } from 'src/context/view-mode.context';
import { isAdmin } from 'src/utils/user';

interface CompanyContactsTabProps {
  company: CompanyDTO;
  onRefresh?: () => void;
}

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  title: string;
  isPrimary: boolean;
  metadata: object | null;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  metadata?: string;
}

// Phone number regex: allows common formats like +1-555-123-4567, (555) 123-4567, 555-123-4567, etc.
const phoneRegex =
  /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}[-\s.]?[0-9]{1,9}$/;

function validateEmail(email: string): boolean {
  if (!email || email.trim() === '') return true; // Empty is valid (optional field)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

function validatePhone(phone: string): boolean {
  if (!phone || phone.trim() === '') return true; // Empty is valid (optional field)
  return phoneRegex.test(phone);
}

export function CompanyContactsTab({
  company,
  onRefresh,
}: CompanyContactsTabProps) {
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const userIsAdmin = isAdmin(currentUser, viewMode);

  const { data: contacts = [], isLoading: isLoadingContacts } = useContacts(
    company.id,
    true,
  );
  const createContactMutation = useCreateContact();
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    title: '',
    isPrimary: false,
    metadata: null,
  });
  const [metadataText, setMetadataText] = useState('');
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const updateContactMutation = useUpdateContact(
    editingContact?.id || '',
    () => {
      onRefresh?.();
      handleModalClose();
    },
  );

  const deleteContactMutation = useDeleteContact(
    contactToDelete?.id || '',
    () => {
      onRefresh?.();
      setDeleteConfirmOpen(false);
      setContactToDelete(null);
    },
  );

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (formData.email && !validateEmail(formData.email)) {
      errors.email = 'Invalid email format';
    }

    if (formData.phone && !validatePhone(formData.phone)) {
      errors.phone = 'Invalid phone number format';
    }

    // Validate metadata JSON if admin and metadata text is provided
    if (userIsAdmin && metadataText.trim()) {
      try {
        JSON.parse(metadataText);
      } catch {
        errors.metadata = 'Invalid JSON format';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateOrUpdateContact = async () => {
    if (!validateForm()) {
      return;
    }

    // Parse metadata if admin
    let parsedMetadata: object | null = null;
    if (userIsAdmin) {
      if (metadataText.trim()) {
        try {
          parsedMetadata = JSON.parse(metadataText);
        } catch {
          // Should not happen due to validation, but handle gracefully
          return;
        }
      } else {
        // Empty string means null (clear metadata)
        parsedMetadata = null;
      }
    }

    if (editingContact) {
      updateContactMutation.mutate({
        name: formData.name,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        title: formData.title || undefined,
        isPrimary: formData.isPrimary,
        ...(userIsAdmin && { metadata: parsedMetadata }),
      });
    } else {
      createContactMutation.mutate(
        {
          companyId: company.id,
          name: formData.name,
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          title: formData.title || undefined,
          isPrimary: formData.isPrimary,
          ...(userIsAdmin && { metadata: parsedMetadata }),
        },
        {
          onSuccess: () => {
            onRefresh?.();
            handleModalClose();
          },
        },
      );
    }
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      email: contact.email || '',
      phone: contact.phone || '',
      title: contact.title || '',
      isPrimary: contact.isPrimary,
      metadata: contact.metadata,
    });
    setMetadataText(
      contact.metadata ? JSON.stringify(contact.metadata, null, 2) : '',
    );
    setContactModalOpen(true);
  };

  const handleDeleteContact = (contact: Contact) => {
    setContactToDelete(contact);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteContact = () => {
    if (contactToDelete) {
      deleteContactMutation.mutate(undefined);
    }
  };

  const handleModalClose = () => {
    setContactModalOpen(false);
    setEditingContact(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      title: '',
      isPrimary: false,
      metadata: null,
    });
    setMetadataText('');
    setFormErrors({});
  };

  const isLoading =
    createContactMutation.isLoading ||
    updateContactMutation.isLoading ||
    deleteContactMutation.isLoading;

  return (
    <>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-medium">Contacts</h2>
          <Button
            onClick={() => setContactModalOpen(true)}
            size="sm"
            className="flex items-center gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Add Contact
          </Button>
        </div>

        {isLoadingContacts && contacts.length === 0 ? (
          <div className="text-center py-8">
            <span className="text-sm text-gray-600">Loading contacts...</span>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <span className="text-xs font-medium text-gray-600">
                    Name
                  </span>
                </TableHead>
                <TableHead>
                  <span className="text-xs font-medium text-gray-600">
                    Email
                  </span>
                </TableHead>
                <TableHead>
                  <span className="text-xs font-medium text-gray-600">
                    Phone
                  </span>
                </TableHead>
                <TableHead>
                  <span className="text-xs font-medium text-gray-600">
                    Title
                  </span>
                </TableHead>
                <TableHead>
                  <span className="text-xs font-medium text-gray-600">
                    Primary
                  </span>
                </TableHead>
                {userIsAdmin && (
                  <TableHead>
                    <span className="text-xs font-medium text-gray-600">
                      Metadata
                    </span>
                  </TableHead>
                )}
                <TableHead>
                  <span className="text-xs font-medium text-gray-600">
                    Actions
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell>
                    <span className="text-sm">{contact.name}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{contact.email || '-'}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{contact.phone || '-'}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{contact.title || '-'}</span>
                  </TableCell>
                  <TableCell>
                    {contact.isPrimary && (
                      <span className="text-xs font-medium text-blue-600">
                        Primary
                      </span>
                    )}
                  </TableCell>
                  {userIsAdmin && (
                    <TableCell>
                      <span className="text-sm font-mono text-xs">
                        {contact.metadata
                          ? JSON.stringify(contact.metadata)
                          : '-'}
                      </span>
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditContact(contact)}
                        className="h-8 w-8 p-0"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteContact(contact)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {contacts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={userIsAdmin ? 7 : 6}>
                    <div className="text-center py-4">
                      <span className="text-sm text-gray-600">
                        No contacts found for this company
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Contact Creation/Edit Modal */}
      <Dialog open={contactModalOpen} onOpenChange={handleModalClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingContact ? 'Edit Contact' : 'Add New Contact'}
            </DialogTitle>
            <DialogDescription>
              {editingContact
                ? 'Update the contact information for this company.'
                : 'Add a new contact for this company.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (formErrors.name) {
                    setFormErrors({ ...formErrors, name: undefined });
                  }
                }}
                placeholder="Enter contact name"
                required
                className={formErrors.name ? 'border-red-500' : ''}
              />
              {formErrors.name && (
                <p className="text-sm text-red-500">{formErrors.name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  if (formErrors.email) {
                    setFormErrors({ ...formErrors, email: undefined });
                  }
                }}
                onBlur={() => {
                  if (formData.email && !validateEmail(formData.email)) {
                    setFormErrors({
                      ...formErrors,
                      email: 'Invalid email format',
                    });
                  }
                }}
                placeholder="Enter email address"
                className={formErrors.email ? 'border-red-500' : ''}
              />
              {formErrors.email && (
                <p className="text-sm text-red-500">{formErrors.email}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => {
                  setFormData({ ...formData, phone: e.target.value });
                  if (formErrors.phone) {
                    setFormErrors({ ...formErrors, phone: undefined });
                  }
                }}
                onBlur={() => {
                  if (formData.phone && !validatePhone(formData.phone)) {
                    setFormErrors({
                      ...formErrors,
                      phone: 'Invalid phone number format',
                    });
                  }
                }}
                placeholder="Enter phone number (e.g., +1-555-123-4567)"
                className={formErrors.phone ? 'border-red-500' : ''}
              />
              {formErrors.phone && (
                <p className="text-sm text-red-500">{formErrors.phone}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Enter job title"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isPrimary"
                checked={formData.isPrimary}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isPrimary: checked === true })
                }
              />
              <Label
                htmlFor="isPrimary"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Set as primary contact
              </Label>
            </div>
            {userIsAdmin && (
              <div className="space-y-2">
                <Label htmlFor="metadata">Metadata (JSON)</Label>
                <Textarea
                  id="metadata"
                  value={metadataText}
                  onChange={(e) => {
                    setMetadataText(e.target.value);
                    if (formErrors.metadata) {
                      setFormErrors({ ...formErrors, metadata: undefined });
                    }
                  }}
                  onBlur={() => {
                    if (metadataText.trim()) {
                      try {
                        JSON.parse(metadataText);
                      } catch {
                        setFormErrors({
                          ...formErrors,
                          metadata: 'Invalid JSON format',
                        });
                      }
                    }
                  }}
                  placeholder='Enter JSON metadata (e.g., {"key": "value"})'
                  className="min-h-[120px] font-mono text-sm"
                />
                {formErrors.metadata && (
                  <p className="text-sm text-red-500">{formErrors.metadata}</p>
                )}
                <p className="text-xs text-gray-500">
                  Enter valid JSON for additional contact metadata
                </p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateOrUpdateContact}
              disabled={!formData.name || isLoading}
            >
              {isLoading
                ? 'Saving...'
                : editingContact
                  ? 'Update Contact'
                  : 'Add Contact'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDeleteContact}
        title="Delete Contact"
        message={`Are you sure you want to delete ${contactToDelete?.name}? This action cannot be undone.`}
        confirmText="Delete"
        loading={isLoading}
        dangerous={true}
      />
    </>
  );
}
